import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Chrome, Shield, Clock, ArrowRight, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PricingCardProps {
  title: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  onSelectPlan?: (planType: 'residential' | 'business') => void;
  planType?: 'residential' | 'business';
  directLink?: string;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  period,
  description,
  features,
  popular,
  onSelectPlan,
  planType,
  directLink
}) => (
  <Card className={`relative ${popular ? 'border-blue-500 shadow-lg' : ''}`}>
    {popular && (
      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500">
        {title === "Annual Plan" ? "Most Popular in Dunblane" : "Most Popular - Quick Start Available!"}
      </Badge>
    )}
    <CardHeader className="text-center">
      <CardTitle className="text-xl">{title}</CardTitle>
      <div className="mt-4">
        <span className="text-3xl font-bold text-blue-600">{price}</span>
        <span className="text-muted-foreground ml-1">{period}</span>
      </div>
      <p className="text-sm text-muted-foreground mt-2">{description}</p>
    </CardHeader>
    <CardContent>
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      <Button 
        className="w-full" 
        variant={popular ? "default" : "outline"}
        onClick={() => {
          if (directLink) {
            window.open(directLink, '_blank');
          } else if (onSelectPlan && planType) {
            onSelectPlan(planType);
          }
        }}
      >
        {directLink ? "Pay with Stripe" : (popular ? "Quick Start - No Account Required" : `Select ${title} Plan`)}
      </Button>
    </CardContent>
  </Card>
);

export default function PricingWithBooking() {
  const [selectedPlan, setSelectedPlan] = useState<'residential' | 'business' | null>(null);
  const [showSignupOptions, setShowSignupOptions] = useState(false);

  const handleSelectPlan = (planType: 'residential' | 'business') => {
    setSelectedPlan(planType);
    setShowSignupOptions(true);
  };

  const handleGoogleSignup = async () => {
    // Store the plan for after authentication
    sessionStorage.setItem('selectedPlan', selectedPlan || '');
    sessionStorage.setItem('postAuthRedirect', '/pricing');
    // This will be implemented when Google OAuth is configured
    console.log('Google signup clicked - plan stored for after auth');
  };

  const handleGuestCheckout = () => {
    // Navigate to guest checkout flow
    window.location.href = `/guest-checkout?plan=${selectedPlan}`;
  };

  const handleEmailSignup = () => {
    // Store the plan for after authentication and redirect to pricing to continue
    sessionStorage.setItem('selectedPlan', selectedPlan || '');
    sessionStorage.setItem('postAuthRedirect', '/pricing');
    window.location.href = `/admin?plan=${selectedPlan}&action=signup`;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">
          Transparent Pricing, No Surprises
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Professional eco-friendly bin washing in Dunblane, Bridge of Allan & Doune. 
          Start your subscription in 3 easy steps.
        </p>
      </div>

      {/* Step-by-step process */}
      <div className="mb-12 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-center mb-6">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
              1
            </div>
            <h3 className="font-semibold mb-2">Choose Your Plan</h3>
            <p className="text-sm text-muted-foreground">
              Select residential (£5/week) or business (£20/week per bin)
            </p>
          </div>
          <div className="text-center">
            <div className="bg-green-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
              2
            </div>
            <h3 className="font-semibold mb-2">Quick Sign-up</h3>
            <p className="text-sm text-muted-foreground">
              Create account with Google, email, or checkout as guest
            </p>
          </div>
          <div className="text-center">
            <div className="bg-purple-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
              3
            </div>
            <h3 className="font-semibold mb-2">We Handle Everything</h3>
            <p className="text-sm text-muted-foreground">
              Automated scheduling, eco-friendly cleaning, SEPA compliance
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      {!showSignupOptions ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-12">
          
          <PricingCard
            title="Grey & Blue Bin Only"
            price="£10"
            period="per month"
            description="Quick payment for grey and blue bin service"
            features={[
              "Monthly bin cleaning service",
              "Grey and blue bins only",
              "Direct Stripe payment",
              "No subscription setup required",
              "Eco-friendly cleaning products",
              "SEPA-compliant disposal",
              "Instant payment processing"
            ]}
            directLink="https://buy.stripe.com/dRmeVcey77QsfBt9APgjC03"
          />

          <PricingCard
            title="Annual Plan"
            price="£210"
            period="per year"
            description="Best value - pay annually and save"
            popular={true}
            features={[
              "Full year of bin cleaning",
              "Save money with annual payment",
              "Direct Stripe payment",
              "No subscription setup required",
              "All bin types included",
              "Priority customer support",
              "Guaranteed service for 12 months"
            ]}
            directLink="https://buy.stripe.com/8x200i1B3Ac60T00fgjC02"
          />

          <PricingCard
            title="Weekly NO Brown Bin"
            price="£15"
            period="per month"
            description="Weekly service excluding brown bin"
            features={[
              "Weekly bin cleaning service",
              "Grey, blue and green bins",
              "No brown bin cleaning",
              "Direct Stripe payment",
              "Eco-friendly cleaning products",
              "SEPA-compliant disposal",
              "Cancel anytime"
            ]}
            directLink="https://buy.stripe.com/aFafZg1LldaM891aETgjC01"
          />

          <PricingCard
            title="Bin Wash Weekly"
            price="£5"
            period="per week"
            description="Quick weekly payment option"
            popular={true}
            features={[
              "Weekly bin cleaning service",
              "All bins included",
              "Direct Stripe payment",
              "Quick setup",
              "Eco-friendly cleaning",
              "SEPA-compliant disposal",
              "No subscription required"
            ]}
            directLink="https://buy.stripe.com/28EfZg89J0o0expcN1gjC00"
          />
        </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-blue-200">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                Complete Your {selectedPlan === 'residential' ? 'Residential' : 'Business'} Subscription
              </CardTitle>
              <p className="text-muted-foreground">
                Choose how you'd like to create your account and start your bin washing service
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Guest Checkout - Primary Option */}
              <Button
                variant="default"
                className="w-full h-14 text-base font-medium bg-green-600 hover:bg-green-700"
                onClick={handleGuestCheckout}
              >
                <Clock className="w-6 h-6 mr-3" />
                <div className="text-left">
                  <div>Start Now - No Account Required</div>
                  <div className="text-xs text-green-100">Fastest way to begin service today</div>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto" />
              </Button>

              {/* Admin Access */}
              <Button
                variant="outline"
                className="w-full h-14 text-base font-medium border-2 hover:border-purple-300"
                onClick={handleEmailSignup}
              >
                <Users className="w-6 h-6 mr-3 text-purple-500" />
                <div className="text-left">
                  <div>Admin Access</div>
                  <div className="text-xs text-muted-foreground">For business owners and administrators</div>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto" />
              </Button>

              <div className="flex items-center justify-center mt-6 p-4 bg-green-50 rounded-lg">
                <Shield className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm text-green-700">
                  Secure checkout • No contracts • Cancel anytime
                </span>
              </div>

              <Button
                variant="ghost"
                className="w-full mt-4"
                onClick={() => setShowSignupOptions(false)}
              >
                ← Back to pricing
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trust badges */}
      <div className="flex flex-wrap justify-center gap-4 mt-12 pt-8 border-t">
        <div className="flex items-center text-sm text-muted-foreground">
          <Shield className="w-4 h-4 mr-2 text-blue-500" />
          SEPA Compliant
        </div>
        <div className="flex items-center text-sm text-muted-foreground">
          <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
          Eco-Friendly
        </div>
        <div className="flex items-center text-sm text-muted-foreground">
          <Users className="w-4 h-4 mr-2 text-purple-500" />
          Locally Owned
        </div>
      </div>
    </div>
  );
}